<template>
  <v-sheet>
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-row>
    </v-overlay>
    <v-card outlined class="w-100 page-card">
      <v-card-title>
        <v-icon large class="pr-2">mdi-account-key</v-icon>
        <h3>{{ $t("userRights") }}</h3>
        <v-spacer></v-spacer>

        <v-row v-if="!showLegend" id="overflowButtons" no-gutters>
          <v-tooltip bottom color="primary">
            <template slot="activator" slot-scope="{ on }">
              <v-btn id="legendButton" v-on="on" color="primary" fab fixed top right @click="showLegend = true">
                <v-icon style="height: unset">mdi-table-of-contents</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("showLegend") }}</span>
          </v-tooltip>
        </v-row>
      </v-card-title>
      <v-card-text>
        <div class="d-flex flex-wrap">
          <v-text-field v-model="userListSearch" :label="$t('search')" style="border-radius: 2em / 5em; max-width: 600px" dense outlined hide-details>
            <template slot="prepend">
              <v-icon v-if="userListSearch == ''">mdi-magnify</v-icon>
              <v-icon @click="userListSearch = ''" color="primary " :style="{ cursor: 'pointer' }" v-if="userListSearch != ''">mdi-cached</v-icon>
            </template>
          </v-text-field>

          <v-select v-model="selectedTeams" :items="teams" class="pl-8" label="Teams" hide-details multiple style="max-width: 300px"></v-select>
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredUserList"
          :items-per-page="-1"
          :search="userListSearch"
          fixed-header
          sort-by="lastName"
          height="100%"
          class="elevation-1 table-header"
          hide-default-footer
          item-key="id"
        >
          <template v-slot:item.lastName="{ item }">
            <v-row justify="center">
              {{ item.name }}
            </v-row>
          </template>
          <template v-slot:item.rights.user="{ item }">
            <v-row justify="center">
              <v-checkbox v-model="item.rights.user"></v-checkbox>
            </v-row>
          </template>
          <template v-slot:item.rights.projectUser="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.projectUser"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.manualTimekeeping="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.manualTimekeeping"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.userManager="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.userManager"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.presentList="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.presentList"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.chefPresentList="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.chefPresentList"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.timeCorrection="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.timeCorrection"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.reviewWorkTime="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.reviewWorkTime"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.reviewWorkTimeTeam="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.reviewWorkTimeTeam"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.projectAndBreaksTimeCorrection="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.projectAndBreaksTimeCorrection"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.canChangeBusinessTripTime="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.canChangeBusinessTripTime"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.canAccessManagement="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.canAccessManagement"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.canAccessAbsenceOverview="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.canAccessAbsenceOverview"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.canAccessAbsenceOverviewTeam="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.canAccessAbsenceOverviewTeam"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.canAccessGeneralReporting="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.canAccessGeneralReporting"></v-checkbox></v-row>
          </template>
          <template v-slot:item.rights.canAccessGeneralReportingTeam="{ item }">
            <v-row justify="center"><v-checkbox v-model="item.rights.canAccessGeneralReportingTeam"></v-checkbox></v-row>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions class="pa-2">
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="saveUserGroups">{{ $t("save") }}</v-btn>
      </v-card-actions>
    </v-card>
    <UserRightsLegend v-if="showLegend" @close="showLegend = false" />
  </v-sheet>
</template>

<script>
import * as ObjectHelpers from "../../helpers/ObjectHelpers";
import { mapGetters } from "vuex";

export default {
  name: "UserGroup",
  route: "UserGroup",
  data: (vm) => ({
    filteredUserList: [],
    loaderOverlay: false,
    allUsers: [],

    showLegend: false,
    userListSearch: "",
    selectedTeams: []
  }),
  components: { UserRightsLegend: () => import("./UserAdministration/UserRightsLegend.vue") },

  computed: {
    headers() {
      return [
        {
          text: this.$t("name"),
          value: "lastName",
          hint: this.$t("name"),
          align: "start",
          sortable: true,
          width: "10%",
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("bookTimes"),
          value: "rights.user",
          hint: this.$t("bookTimes"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("projectTime"),
          value: "rights.projectUser",
          hint: this.$t("projectTime"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("manualTimekeeping"),
          value: "rights.manualTimekeeping",
          hint: this.$t("manualTimekeeping"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("userAdministration"),
          value: "rights.userManager",
          hint: this.$t("userAdministration"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("presentListUser"),
          value: "rights.presentList",
          hint: this.$t("presentListUser"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("presentListChef"),
          value: "rights.chefPresentList",
          hint: this.$t("presentListChef"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("timeCorrection"),
          value: "rights.timeCorrection",
          hint: this.$t("timeCorrection"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("reviewWorkTime"),
          value: "rights.reviewWorkTime",
          hint: this.$t("reviewWorkTime"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("reviewWorkTimeTeam"),
          value: "rights.reviewWorkTimeTeam",
          hint: this.$t("reviewWorkTimeTeam"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("projectAndPauseTimeManipulation"),
          value: "rights.projectAndBreaksTimeCorrection",
          hint: this.$t("projectAndPauseTimeManipulation"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("canChangeBusinessTripTime"),
          value: "rights.canChangeBusinessTripTime",
          hint: this.$t("canChangeBusinessTripTime"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("canAccessManagement"),
          value: "rights.canAccessManagement",
          hint: this.$t("canAccessManagement"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("canAccessAbsenceOverview"),
          value: "rights.canAccessAbsenceOverview",
          hint: this.$t("canAccessAbsenceOverview"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("canAccessAbsenceOverviewTeam"),
          value: "rights.canAccessAbsenceOverviewTeam",
          hint: this.$t("canAccessAbsenceOverviewTeam"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("canAccessGeneralReporting"),
          value: "rights.canAccessGeneralReporting",
          hint: this.$t("canAccessGeneralReporting"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        },
        {
          text: this.$t("canAccessGeneralReportingTeam"),
          value: "rights.canAccessGeneralReportingTeam",
          hint: this.$t("canAccessGeneralReportingTeam"),
          align: "center",
          sortable: false,
          class: "font-weight-bold subheading"
        }
      ];
    },
    ...mapGetters({
      teams: "users/teams"
    })
  },
  watch: {
    selectedTeams(val) {
      let users = [];
      for (let user of this.allUsers) {
        let teamFound = user.teams.find((item) => val.some((team) => team == item.Team));
        if (teamFound) users.push(user);
        else if (val.includes(this.$t("all"))) users.push(user);
      }
      this.filteredUserList = users;
    }
  },
  async created() {
    this.loaderOverlay = true;
    await this.getUserWithGroups();
    await this.getTeams();
    this.loaderOverlay = false;
  },
  methods: {
    async getUserWithGroups() {
      await this.$store.dispatch("userRights/getUserWithGroups").then((res) => {
        this.filteredUserList = res.data.data;
        this.allUsers = [...res.data.data];
      });
    },
    async getTeams() {
      await this.$store.dispatch("users/getTeams");
      this.selectedTeams = [this.teams[0]];
    },
    async mapChangedValues(userList) {
      const formerRights = await this.$store.dispatch("userRights/getUserWithGroups").then((res) => res?.data?.data);
      if (!formerRights) return;

      const authorId = await this.getCurrentUserId();

      return userList.map((e) => {
        const corresponding = formerRights.find((right) => right.id === e.id);

        let changedKeys = ObjectHelpers.getAllChangedKeys(e, corresponding).map(({ child }) => child);
        changedKeys = changedKeys.map((key) => {
          const ret = {
            key,
            granted: undefined
          };

          const formerValue = corresponding.rights[key];
          const currentValue = e.rights[key];

          if (formerValue === currentValue) return;

          ret.granted = !formerValue;

          return ret;
        });

        e.changedKeys = changedKeys;
        e.authorId = authorId;

        return e;
      });
    },
    async getCurrentUserId() {
      return this.$store.getters["user/user"].SAPEmpId;
    },
    async saveUserGroups() {
      this.loaderOverlay = true;
      // const formerRights = await this.$store.dispatch("userRights/getUserWithGroups").then((res) => res?.data?.data);
      // if (!formerRights) return;

      // const changedUsers = this.filteredUserList.filter((e) => {
      //   const corresponding = formerRights.find((right) => right.id === e.id);

      //   return ObjectHelpers.hasChanged(e, corresponding);
      // });

      // const mapped = await this.mapChangedValues(changedUsers);

      // if (mapped.length === 0) {
      //   this.loaderOverlay = false;
      //   return;
      // }

      await this.$store
        .dispatch("userRights/saveUserGroups", this.filteredUserList)
        .then(async () => {
          await this.getUserWithGroups();
        })
        .catch((err) => {
          if (err.response.status != 200) {
            this.$eventHub.$emit("showError", err.response.data.error, "bottom", "right");
          }
        });

      this.loaderOverlay = false;
    }
  }
};
</script>

<style scoped>
.page-card {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 125px);
}
.page-card .v-card__text {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-card .v-data-table {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.page-card .v-data-table__wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.v-card__title h1,
.v-card__title h2,
.v-toolbar__title {
  font-size: 16px !important;
  line-height: 24px !important;
  font-weight: 400 !important;
  font-family: Roboto, sans-serif !important;
}
</style>
